import React from "react";
import { Link } from "react-router-dom";
import Section, { SectionHead } from "../layout/Section";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import MicroLabel from "../ui/MicroLabel";
import TicketCard from "../ui/TicketCard";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./ServicesGrid.module.css";

/* ServicesGrid — "what we do": four capability tickets on paper.
   SectionHead (micro label, mixed sans / serif h2, lead, the section's one lime
   pill in the action slot) over a row of notched TicketCards. Each ticket is a
   top strip (index + icon) ruled off from a clamped title, a clamped detail and
   a .link-arrow pinned to a shared baseline. The link is stretched over the
   whole ticket, so a card is one click target and one tab stop.

   The lead is the subtitle of the page the pill links to (/services), read from
   SITE_CONTENT.pages — never the hero subtitle: the Manifesto directly above
   already sets that sentence as its body line, and both would share a screen.

   Section owns surface + rhythm + container; this module only styles what lives
   inside. The h2 is wiped by HeadingWipe (App.js) — it is never wrapped in
   Reveal here. */

// TODO(content): UI labels for this section
const SECTION = {
  eyebrow: "What we do",
  // The previous title ("Capabilities that turn ideas into dependable
  // software") re-lined into a two-line headline with one serif accent word.
  // Not "software": that is the Manifesto's first serif word, one screen up.
  title: (
    <>
      Turning ideas into <em>dependable</em> software
    </>
  ),
  titleId: "services-title",
  ctaLabel: "All services",
  ctaTo: "/services"
};

// TODO(content): replace with real Appinfigo content
// Four capability groups. Each links to its real page in SITE_CONTENT.pages.
const SERVICES = [
  {
    id: "product-engineering",
    icon: "product",
    title: "Digital product engineering",
    description:
      "Whiteboard sketch to deployed product — web, mobile and platform builds by one senior team.",
    linkLabel: "Engineering",
    linkTitle: "Engineering services",
    to: "/product-engineering"
  },
  {
    id: "cloud-modernization",
    icon: "cloud",
    title: "Cloud & platform modernization",
    description:
      "Move your stack to AWS, GCP or Vercel, with CI/CD that deploys and hosting that costs less.",
    linkLabel: "Cloud",
    linkTitle: "Cloud services",
    to: "/cloud-modernization"
  },
  {
    id: "data-and-ai",
    icon: "ai",
    title: "Data, AI & analytics",
    description:
      "Intelligent search, document Q&A, copilots and automation flows your users actually use.",
    linkLabel: "AI & data",
    // Starts with the visible label, character for character (WCAG 2.5.3).
    linkTitle: "AI & data services",
    to: "/data-and-ai"
  },
  {
    id: "application-security",
    icon: "security",
    title: "Application security",
    description:
      "Access control, encryption, audit logging and an OWASP review before anything ships.",
    linkLabel: "Security",
    linkTitle: "Security services",
    to: "/cybersecurity"
  }
];

/* The lead, in the site's own words: the subtitle of the page the pill opens.
   Nothing is written here — if that subtitle changes, this line changes with
   it, and if the page is ever removed SectionHead simply renders no lead. */
const servicesPage =
  (SITE_CONTENT.pages || []).find((page) => page.path === SECTION.ctaTo) || {};

const LEAD =
  typeof servicesPage.subtitle === "string" ? servicesPage.subtitle.trim() : "";

const indexLabel = (index) => String(index + 1).padStart(2, "0");

export default function ServicesGrid() {
  return (
    <Section
      tone="paper"
      size="lg"
      id="services"
      aria-labelledby={SECTION.titleId}
    >
      <SectionHead
        eyebrow={SECTION.eyebrow}
        title={SECTION.title}
        titleId={SECTION.titleId}
        lead={LEAD}
        action={
          <Link to={SECTION.ctaTo} className="btn">
            {SECTION.ctaLabel}
            <Icon name="arrow-up-right" size={18} />
          </Link>
        }
      />

      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- list-style: none strips list semantics in Safari / VoiceOver; the explicit role restores them */}
      <ul role="list" className={styles.grid}>
        {SERVICES.map((service, index) => (
          <Reveal
            as="li"
            key={service.id}
            className={styles.cell}
            direction="up"
            delay={index * 80}
          >
            <TicketCard tone="paper" className={styles.card}>
              <div className={styles.strip}>
                <MicroLabel className={styles.index} aria-hidden="true">
                  {indexLabel(index)}
                </MicroLabel>
                <Icon name={service.icon} size={28} className={styles.icon} />
              </div>

              <h3 className={styles.title}>{service.title}</h3>
              <p className={styles.detail}>{service.description}</p>

              <Link
                to={service.to}
                className={`link-arrow ${styles.link}`}
                aria-label={service.linkTitle}
              >
                {service.linkLabel}
              </Link>
            </TicketCard>
          </Reveal>
        ))}
      </ul>
    </Section>
  );
}
